import { Box, Button, ButtonGroup, Heading, List, Modal, Text, TextButton } from '@zillow/constellation-10';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import type { DatarouterError } from '@zg-rentals/web-base';
export type FreemiumDatarouterError = {
  errorDetails?: {
    listingActivationFailed?: boolean;
    activationError?: {
      message?: string;
    };
    listing?: {
      [key: string]: string;
    };
    floorPlans?: {
      [floorPlanId: string]: {
        [field: string]: string;
      };
    };
  };
} & DatarouterError;

type Props = {
  onClose: () => void;
  onPublishForFreeClick: () => void;
  onTryAgainClick: () => void;
  error?: FreemiumDatarouterError;
  isPremiumMidfunnelPayment: boolean;
  selectedProductType: number;
};

type ValidationError = {
  key: string;
  message: string;
};

type ErrorData = NonNullable<NonNullable<FreemiumDatarouterError['errorDetails']>>;

const getAllValidationErrors = (errorData?: ErrorData): Array<ValidationError> => {
  const errors: Array<ValidationError> = [];

  if (errorData?.listing) {
    Object.entries(errorData.listing).forEach(([field, message]) => {
      if (typeof message === 'string') {
        errors.push({ key: `listing-${field}`, message });
      }
    });
  }

  if (errorData?.floorPlans) {
    Object.entries(errorData.floorPlans).forEach(([floorPlanId, floorPlan]) => {
      if (floorPlan) {
        Object.entries(floorPlan).forEach(([field, message]) => {
          if (typeof message === 'string') {
            errors.push({ key: `floorPlan-${floorPlanId}-${field}`, message });
          }
        });
      }
    });
  }

  return errors;
};

const FreemiumErrorModal = ({
  onClose,
  onPublishForFreeClick,
  onTryAgainClick,
  error,
  isPremiumMidfunnelPayment,
  selectedProductType,
}: Props) => {
  const getErrorMessage = (err?: FreemiumDatarouterError) => {
    if (!err) return null;

    const { errorDetails, message: errorMessage } = err;
    const { listingActivationFailed, activationError } = errorDetails || {};

    let message = errorMessage;

    if (listingActivationFailed) {
      if (activationError?.message) {
        message = activationError?.message;
      }
    }

    return message;
  };

  const cancelProps: { submitText: string; onSubmit: () => void; cancelText?: string; onCancel?: () => void } = {
    submitText: 'Try again',
    onSubmit: selectedProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? onTryAgainClick : onPublishForFreeClick,
  };
  if (isPremiumMidfunnelPayment) {
    cancelProps.cancelText = 'Cancel and edit listing';
    cancelProps.onCancel = onClose;
  } else if (selectedProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM) {
    cancelProps.cancelText = 'Publish for free';
    cancelProps.onCancel = onPublishForFreeClick;
  }

  const validationErrors = getAllValidationErrors(error?.errorDetails);

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{
        base: 'fullScreen',
        lg: 'md',
      }}
      header={<Heading level={2}>Error</Heading>}
      body={
        <>
          <Text asChild>
            <p>
              {getErrorMessage(error) ||
                `An error occurred with your payment method and your premium upgrade for this listing couldn’t be processed. Please retry or use another payment method to upgrade to premium. Or, if you prefer, you can publish your listing for free now and upgrade to premium later.`}
            </p>
          </Text>
          {validationErrors.length > 0 && (
            <Box css={{ marginTop: '400' }}>
              <Text asChild>
                <p>Details:</p>
              </Text>
              <List.Root>
                {validationErrors.map(({ key, message }) => (
                  <List.Item key={key}>{message}</List.Item>
                ))}
              </List.Root>
            </Box>
          )}
        </>
      }
      footer={
        <ButtonGroup aria-label="modal actions">
          <TextButton onClick={cancelProps?.onCancel}>{cancelProps?.cancelText}</TextButton>
          <Button emphasis="filled" tone="brand" onClick={cancelProps.onSubmit}>
            {cancelProps.submitText}
          </Button>
        </ButtonGroup>
      }
    ></Modal>
  );
};

export default FreemiumErrorModal;
